import logo from "@/assets/logo.png";
import { NavAssets } from "@/components/nav-assets";
import { NavMain } from "@/components/nav-main";
import { NavSecondary } from "@/components/nav-secondary";
import { NavUser } from "@/components/nav-user";
import { NavVersions } from "@/components/nav-versions";
import { OrgSwitcher } from "@/components/org-switcher";
import { PYLON_SUPPORT_FORM_URL, SupportRequestDialog } from "@/components/support-request-dialog";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { useAssets } from "@/hooks/use-assets";
import { useFileShareConfig } from "@/hooks/use-file-share";
import { useOrgStore, useUserStore } from "@/stores";
import { BookOpenText, Download, FolderOpen, HelpCircle, Key, ShieldCheck, Users } from "lucide-react";
import * as React from "react";
import { Link } from "react-router";

const data = {
  navMain: [
    {
      title: "Documentation",
      url: "https://docs.istaridigital.com/",
      icon: BookOpenText,
      external: true,
    },
    {
      title: "Support Request",
      url: PYLON_SUPPORT_FORM_URL,
      icon: HelpCircle,
      embedded: true,
    },
  ],
  navLicenses: [
    {
      title: "License Management",
      url: "/licenses",
      icon: Key,
    },
  ],
  navFileShare: [
    {
      title: "File Share",
      url: "/file-share",
      icon: FolderOpen,
    },
  ],
  navUsers: [
    {
      title: "User Management",
      url: "/users",
      icon: Users,
    },
  ],
  navAdmin: [
    {
      title: "Download History",
      url: "/downloads",
      icon: Download,
    },
  ],
  navSecurityStatements: [
    {
      title: "Security Statements",
      url: "/security-statements",
      icon: ShieldCheck,
    },
  ],
};

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const [supportDialogOpen, setSupportDialogOpen] = React.useState(false);
  const user = useUserStore.use.user();
  const selectedOrgId = useOrgStore.use.selectedOrgId();
  // Hide the entry only after a successful query returns zero statements — on
  // failure or while unknown, keep the navigation reachable.
  const assetsQuery = useAssets();
  const hasSecurityStatements = assetsQuery.isError || (assetsQuery.data?.securityStatements?.length ?? 0) > 0;
  // A flagged feature stays hidden until the backend confirms it is enabled.
  const fileShareConfigQuery = useFileShareConfig();
  const fileShareEnabled = fileShareConfigQuery.data?.enabled === true;

  const hasOrgAccess = user?.superAdmin || (user?.organizations && user.organizations.length > 0);

  // Only use selectedOrgId for the ORGOWNER check if it's actually present in the user's token
  // permissions. A stale localStorage value pointing to an inaccessible org would otherwise hide
  // admin nav items even though the user is an ORGOWNER of their real org.
  const selectedOrgIsAccessible = selectedOrgId ? user?.permissions?.some((perm) => perm.id === selectedOrgId) : false;

  const isAdminForSelectedOrg =
    user?.superAdmin ||
    user?.permissions?.some(
      (perm) =>
        (selectedOrgIsAccessible ? perm.id === selectedOrgId : perm.default) && perm.roles?.includes("ORGOWNER"),
    );

  return (
    <Sidebar collapsible="none" className="sticky top-0 h-screen" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <Link to="/">
              <img src={logo} alt="Logo" className="h-9 dark:invert" />
            </Link>
          </SidebarMenuItem>
        </SidebarMenu>
        <OrgSwitcher />
      </SidebarHeader>
      <SidebarContent className="flex h-full flex-col">
        <div className="flex-1 overflow-y-auto">
          {hasSecurityStatements && <NavSecondary items={data.navSecurityStatements} className="" />}
          <NavVersions />
          <NavAssets />
        </div>
        <div className="mt-auto">
          <NavMain items={data.navMain} onEmbeddedItemClick={() => setSupportDialogOpen(true)} />
          {hasOrgAccess && <NavSecondary items={data.navLicenses} />}
          {hasOrgAccess && fileShareEnabled && <NavSecondary items={data.navFileShare} />}
          {isAdminForSelectedOrg && <NavSecondary items={data.navUsers} />}
          {user?.superAdmin && <NavSecondary items={data.navAdmin} />}
        </div>
      </SidebarContent>
      <SidebarFooter>
        <NavUser />
      </SidebarFooter>
      <SupportRequestDialog open={supportDialogOpen} onOpenChange={setSupportDialogOpen} />
    </Sidebar>
  );
}
